import { Field } from './Field.js';
import { makeEnum } from '../Base.js';
import { valOr, GraphNode, DFSHelper, applyEllipse, isObject, } from '../SharedUtils.js';
import { Units, makeValStr } from './Units.js';

export class FieldWithVariableUnits extends Field {
  constructor(data) {
    super(data);

    this.unitOptions = data.unitOptions;
    this.serFields = [
      ...this.serFields,
      'units',
    ];
  }

  // Accepts either a plain value (sets it in the field's *current* unit,
  // same as the base Field) or {value, units} to switch representations —
  // e.g. {value: 10, units: Units.ACH} for "set to 10 ACH" when the field
  // currently shows cfm. Units are applied before the value so the value is
  // interpreted (and converted/stored) under the new unit, not the old one.
  applyAgentValue(value) {
    if (this.isOutput) {
      throw new Error(`'${this.name}' is a computed/output field and can't be set directly.`);
    }
    if (!isObject(value)) {
      return super.applyAgentValue(value);
    }
    if (value.units !== undefined) {
      if (!this.unitOptions.includes(value.units)) {
        throw new Error(
          `Invalid units '${value.units}' for '${this.name}'. Valid options: ${this.unitOptions.join(', ')}`
        );
      }
      this.units = value.units;
    }
    if (value.value !== undefined) {
      this.setDisplayValueAsUser(Number(value.value));
    }
  }
}

export class FieldWithDefault extends Field {
  constructor(data) {
    super(data);
    // Use aux data to track defaults
    this.data = {
      defaultValue: this._value,
      useDefault: true,
    }
  }

  useDefault() {
    return this.data.useDefault;
  }

  getDefaultValueStr() {
    let defaultValDisplayUnits = Units.convertValue(
      this.data.defaultValue,
      this.units,
      this.getDisplayUnits()
    );
    return makeValStr(defaultValDisplayUnits, this.getDisplayUnits(), {includeSpace: true});
  }

  getValue() {
    return this.data.useDefault ? this.data.defaultValue : this._value;
  }

  getDisplayValue() {
    if (!this.data.useDefault) {
      return this._displayValue;
    }
    return Units.convertValue(this.data.defaultValue, this.units, this.getDisplayUnits());
  }

  getDefaultValue() {
    return this.data.defaultValue;
  }

  // Setting a value (the standard Field write API) is how a caller overrides
  // the default — mirrors FieldInputWithOverride.vue's addOverride(). Without
  // this, setValueAsUser()/setDisplayValueAsUser() would write _value/_displayValue
  // but getValue() would keep returning the default, silently ignoring the write.
  setValueAsUser(newVal) {
    this.data.useDefault = false;
    super.setValueAsUser(newVal);
  }

  setDisplayValueAsUser(newDisplayVal) {
    this.data.useDefault = false;
    super.setDisplayValueAsUser(newDisplayVal);
  }
}

export class MultiTieredSelect extends Field {
  constructor(data) {
    data.type = 'MultiTieredSelect';
    data.value = valOr(data.value, []);
    super(data);
    this.allowEmpty = valOr(data.allowEmpty, false);
    this.emptyText = valOr(data.emptyText, 'None');

    this.optionsMap = data.optionsMap;
    if ('defaultValue' in data) {
      this._value = data.defaultValue;
    }
  }

  postReadFromJson(data) {
    // Important - call Field.postReadFromJson to handle the old format
    super.postReadFromJson(data);

    // This is required to fixup some bad data from older versions
    if (typeof this._value === 'string') {
      this._value = []
    }
  }

  getOtherObjErrors() {
    let errors = []
    if (!this.allowEmpty && this._value.length === 0) {
      errors.push('Must choose a value.');
    } else if (!this.hasValidPath()){
      errors.push('Invalid item selected. Please change.');
    }
    return errors;
  }

  isEmpty() {
    return this._value.length === 0;
  }

  getShortValue() {
    if (!this._value.length) {
      return this.emptyText;
    }
    let labels = this.getLabels();
    return applyEllipse(labels[labels.length - 1], 35);
  }

  getLabels() {
    if (!this.hasValidPath()) {
      return []
    }
    let labels = []
    let curDict = this.optionsMap;
    for (let i = 0; i < this._value.length; i++) {
      let part = this._value[i]
      let enumType = curDict.type || curDict;
      labels.push(enumType._labels[part]);
      if (i + 1 < this._value.length) {
        curDict = curDict.children[part];
      }
    }
    return labels;
  }

  getLastLabel() {
    return this.getLabels()[this.getLabels().length - 1];
  }

  lookupPath(path) {
    if (path.length === 0) {
      return {
        isLeaf: false,
        value: this.optionsMap,
      };
    }
    let curObj = this.optionsMap;
    let isLeaf = false;
    for (let i = 0; i < path.length; i++) {
      let part = path[i];
      if ('children' in curObj) {
        if (!(part in curObj.children)) {
          return null;
        }
        curObj = curObj.children[part];
        //console.log(`Found part ${part} - `, curObj);
      } else {
        // Leaf node, curObj is an enum
        if (i == path.length - 1) {
          curObj = curObj[part];
          isLeaf = true;
          //console.log(`Found leaf part ${part} - `, curObj);
        } else {
          // We hit a leaf too early
          return null;
        }
      }
    }
    return {
      isLeaf: isLeaf,
      value: curObj,
    }
  }

  hasValidPath() {
    if (this._value.length === 0) {
      return this.allowEmpty;
    }
    let pathValue = this.lookupPath(this._value);
    return pathValue !== null && pathValue.isLeaf;
  }

  // Flattens the full options tree into every valid leaf path, as
  // {path: [...], label: 'Category > Type'}. Used to expose valid values to
  // the AI assistant (see FieldCollector.js), which otherwise has no way to
  // discover what a MultiTieredSelect field's tiered enum tree contains.
  getAllChoices() {
    let results = [];
    let walk = (node, pathSoFar, labelsSoFar) => {
      let enumType = node.type || node;
      for (const key of enumType._keys) {
        let label = enumType._labels[key];
        if (node.children) {
          walk(node.children[key], [...pathSoFar, key], [...labelsSoFar, label]);
        } else {
          results.push({ path: [...pathSoFar, key], label: [...labelsSoFar, label].join(' > ') });
        }
      }
    }
    walk(this.optionsMap, [], []);
    return results;
  }


  getOptionsForPath(path) {
    //console.log('Looking up path:', path)
    let pathValue = this.lookupPath(path);
    if (pathValue === null) {
      throw new Error(`Invalid path: ${path.join('/')} (for ${this.name})`);
    }
    if (pathValue.isLeaf) {
      //console.log("Got leaf path: ", pathValue.value);
      return null;
    }
    //console.log(`Got options for path: ${path.join('/')}`, pathValue.value);
    return pathValue.value;
  }

  isLeafPath(path) {
    let pathValue = this.lookupPath(path);
    return pathValue !== null && pathValue.isLeaf;
  }

  /*
  Helper for making an options map from table data
  */
  static makeOptionsMapFromTableRows(rowsData, colNames) {
    // Create a list of paths
    let paths = []
    for (const row of rowsData) {
      let path = []
      for (const colName of colNames) {
        path.push(row[colName])
      }
      paths.push(path)
    }
    //console.log("Paths: ", paths);

    let optionsTree = GraphNode.makeTreeFromPaths(paths);
    //console.log("Options tree: ", optionsTree);

    // Make the options map by DFS traversal of the tree
    let dfsHelper = new DFSHelper();
    let optionsMap = dfsHelper.doDFS(optionsTree, (node, resultsMap) => {
      //console.log("Visiting: ", node.id);
      if (node.isLeaf()) {
        return;
      }

      let result = null
      let isLeafEnum = node.children[0].isLeaf();
      if (isLeafEnum) {
        // This node is a leaf enum (children are entries)
        let enumData = {}
        for (const childNode of node.children) {
          enumData[childNode.id] = childNode.data.label;
        }
        result = makeEnum(enumData);
      } else {
        // This node is a parent enum (children are enums)
        let parentEnumData = {}
        let childMap = {}
        for (const childNode of node.children) {
          parentEnumData[childNode.id] = childNode.data.label;
          childMap[childNode.id] = resultsMap[childNode.id];
        }
        result = {
          type: makeEnum(parentEnumData),
          children: childMap,
        }
      }
      return result;
    });
    //console.log("Options map: ", optionsMap);

    return optionsMap;
  }
}